import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DatastoreService } from '@src/app/helpers/datastore/datastore.service';
import { ProjectdashCalculationService } from './service/projectdash-calculation.service';
import { ProjectsPerMonthData } from './model/projects-per-month';
import { ExecutionSpeedData } from './model/execution-speed-data';
import { WeeklyCapacityData } from './model/weekly-capacity-data';
import { TransPipe } from '@src/app/helpers/trans/trans.pipe';

/**
 * ProjectDash Component
 * 
 * Displays key organizational metrics:
 * - Projects Per Month: 6-month project completion trend (bar chart)
 * - Execution Speed: 24-hour team velocity percentage (circle chart)
 * - Weekly Capacity: Current week capacity vs planned work (line chart)
 * 
 * Subscribes to DatastoreService observables and recalculates metrics
 * whenever data changes. Uses UntilDestroy for automatic cleanup.
 */
@UntilDestroy()
@Component({
  selector: 'app-projectdash',
  templateUrl: './projectdash.component.html',
  styleUrl: './projectdash.component.css',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    TransPipe
  ]
})
export class ProjectdashComponent implements OnInit {
  @ViewChild('lineChartRef') lineChartRef?: ElementRef<HTMLDivElement>;

  // Calculated metrics
  projectsPerMonth: ProjectsPerMonthData[] = [];
  executionSpeed: ExecutionSpeedData | null = null;
  weeklyCapacity: WeeklyCapacityData[] = [];
  tooltip = { visible: false, x: 0, y: 0, text: '' };

  // Loading and error states
  isLoading = true;
  errorMessage: string | null = null;

  constructor(
    private datastore: DatastoreService,
    private calculations: ProjectdashCalculationService
  ) { }

  ngOnInit(): void {
    // Subscribe to all data sources and recalculate metrics on any change
    combineLatest([
      this.datastore.projectList$,
      this.datastore.orgMembers$,
      this.datastore.lightTaskList$
    ])
      .pipe(untilDestroyed(this))
      .subscribe({
        next: ([projects, persons, tasks]) => {
          try {
            // Calculate all metrics with fresh data
            this.projectsPerMonth = this.calculations.calculateProjectsPerMonth(projects);
            this.executionSpeed = this.calculations.calculateExecutionSpeed(projects, persons, tasks);
            this.weeklyCapacity = this.calculations.calculateWeeklyCapacity(projects, persons);

            this.isLoading = false;
            this.errorMessage = null;
          } catch (error) {
            console.error('Error calculating metrics:', error);
            this.errorMessage = 'Failed to calculate dashboard metrics';
            this.isLoading = false;
          }
        },
        error: (error) => {
          console.error('Error loading dashboard data:', error);
          this.errorMessage = 'Failed to load dashboard data';
          this.isLoading = false;
        }
      });
  }

  // ==================== SVG Helper Methods ====================

  /**
   * Generate SVG polyline points for capacity line
   * Maps daily capacity values to SVG coordinates
   */
  getCapacityLinePoints(): string {
    if (this.weeklyCapacity.length === 0) {
      return '';
    }

    return this.weeklyCapacity.map((day, i) =>
      `${this.getXPosition(i)},${this.getYPosition(day.capacity)}`
    ).join(' ');
  }

  /**
   * Generate SVG polyline points for planned work line
   * Maps daily planned work values to SVG coordinates
   */
  getPlannedWorkLinePoints(): string {
    if (this.weeklyCapacity.length === 0) {
      return '';
    }

    return this.weeklyCapacity.map((day, i) =>
      `${this.getXPosition(i)},${this.getYPosition(day.plannedWork)}`
    ).join(' ');
  }

  /**
   * Calculate X coordinate for a weekday (index 0-4)
   * Distributes evenly across 500px width
   */
  getXPosition(index: number): number {
    return 50 + (index * 90); // Spread across 500px: 50 + (0..4) * 90
  }

  /**
   * Calculate Y coordinate for a value
   * Scales to fit 200px height, with origin at top
   */
  getYPosition(value: number): number {
    if (this.weeklyCapacity.length === 0) {
      return 150;
    }

    const allValues = this.weeklyCapacity.flatMap(d => [d.capacity, d.plannedWork]);
    const maxValue = Math.max(...allValues, 1); // Ensure at least 1 to avoid division by 0

    // SVG coordinates: top is 0, so we subtract from max height
    return 150 - (value / maxValue * 140);
  }

  /**
   * Get maximum project count for scaling bar chart
   */
  getMaxProjectCount(): number {
    if (this.projectsPerMonth.length === 0) {
      return 1;
    }
    return Math.max(...this.projectsPerMonth.map(p => p.count), 1);
  }

  showTooltip(event: MouseEvent, weekIndex: number): void {
    this.tooltip.visible = true;
    const day = this.weeklyCapacity[weekIndex];
    this.tooltip.text = `Week ${weekIndex + 1}: Planned ${day.plannedWork} • Capacity ${day.capacity}`;
    this.updateTooltipPosition(event);
  }

  moveTooltip(event: MouseEvent): void {
    if (!this.tooltip.visible) {
      return;
    }
    this.updateTooltipPosition(event);
  }

  hideTooltip(): void {
    this.tooltip.visible = false;
  }

  private updateTooltipPosition(event: MouseEvent): void {
    const rect = this.lineChartRef?.nativeElement.getBoundingClientRect();
    if (!rect) {
      return;
    }

    this.tooltip.x = event.clientX - rect.left;
    this.tooltip.y = event.clientY - rect.top;
  }
}
